import { readingTime } from '@tryghost/helpers'
import { H2, H3, Link, P1, P3 } from 'components'
import PropTypes from 'prop-types'
import React, { Fragment } from 'react'
import styled from 'styled-components'
import { color, devices, size } from 'theme'

const defaultImageSrc = '/images/blog-generic.jpg'

const Wrapper = styled.div`
  display: flex;
  flex-flow: column;
  gap: ${size(2)};
  margin-bottom: ${size(4)};

  @media ${devices.medium} {
    flex-flow: row;
    gap: ${size(4)};
  }
`
const Image = styled.div`
  flex: 0 0 100%;
  border: 2px solid transparent;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover,
  &:focus {
    border-color: ${color('black')};
  }

  img {
    display: block;
  }

  @media ${devices.medium} {
    flex: 0 0 30%;
  }
`

const ImageLink = styled.a`
  width: 100%;
  height: 100%;
`

const Content = styled.div`
  grid-column: span 8;

  @media ${devices.medium} {
    grid-column: ${({ $small }) => ($small ? 'span 6' : 'span 5')};
  }
`

const Tags = styled.div`
  display: none;
  flex-flow: row wrap;
  align-items: center;
  margin-bottom: ${size(2)};
`

const Tag = styled(Link)`
  text-decoration: none;
  color: ${color('blue')};
  font-size: 1rem;
  line-height: 1rem;
  white-space: nowrap;
  font-weight: normal;
  border: none;

  &:hover {
    text-decoration: underline;
    border: none;
  }
`

const TagDivider = styled.span`
  color: ${color('lightGray')};
  margin: ${size(0, 1)};
`

const StyledP3 = styled(P3)`
  color: ${color('mediumGray')};
`

const PostCard = ({ post, pageContext, small }) => {
  const postUrl = `${pageContext.prefix}${post.slug}/`

  const {
    title,
    feature_image,
    feature_image_alt,
    published_at_pretty,
    tags,
    authors,
  } = post

  const [author] = authors

  return (
    <Wrapper>
      <Image>
        <ImageLink href={postUrl}>
          <img
            src={feature_image || defaultImageSrc}
            alt={feature_image_alt || title}
          />
        </ImageLink>
      </Image>
      <Content $small={small}>
        <StyledP3 $mb={1}>
          <Link to={`/blog/author/${author.slug}`}>{author.name}</Link> •{' '}
          {published_at_pretty} • {readingTime(post)}
        </StyledP3>
        {small ? (
          <H3 $mb={1} $mt={0}>
            <a href={postUrl}>{title}</a>
          </H3>
        ) : (
          <H2 $mb={1} $mt={0}>
            <a href={postUrl}>{title}</a>
          </H2>
        )}
        <Tags>
          {!small &&
            tags.map(({ name, slug }, i) => (
              <Fragment key={name}>
                <Tag to={`/blog/tag/${slug}/`}>{name}</Tag>
                {i < tags.length - 1 && <TagDivider>|</TagDivider>}
              </Fragment>
            ))}
        </Tags>
        {!small && <P1>{post.excerpt}</P1>}
      </Content>
    </Wrapper>
  )
}

PostCard.propTypes = {
  post: PropTypes.shape({
    slug: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    authors: PropTypes.arrayOf(PropTypes.object),
    feature_image: PropTypes.string,
    feature_image_alt: PropTypes.string,
    published_at_pretty: PropTypes.string,
    featured: PropTypes.bool,
    tags: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
      })
    ),
    excerpt: PropTypes.string.isRequired,
  }).isRequired,
}

export default PostCard
